/**
 * Converts a sentence to title case format.
 *
 * This function takes a string and capitalizes the first letter of each word,
 * while converting the rest of the letters to lowercase. Words are separated by spaces.
 *
 * @param sentence - The input string to convert to title case
 * @returns The title case formatted string, or empty string if input is invalid
 *
 * @example
 * ```typescript
 * toTitleCase('hello world') // Returns: 'Hello World'
 * toTitleCase('UPPERCASE TEXT') // Returns: 'Uppercase Text'
 * toTitleCase('mixed Case Words') // Returns: 'Mixed Case Words'
 * toTitleCase('') // Returns: ''
 * toTitleCase('   ') // Returns: ''
 * ```
 */
export const toTitleCase = (sentence: string) => {
    if (typeof sentence !== 'string' || sentence.trim().length === 0) {
        return ''
    }

    const lowercasedSentence = sentence.toLowerCase()

    const words = lowercasedSentence.split(' ')

    const titleCaseWords = words.map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1)
    })

    return titleCaseWords.join(' ')
}

/**
 * Safely converts a string, number, or undefined value to a number.
 *
 * This function attempts to convert various input types to a number.
 * It handles edge cases like zero values and returns null for invalid inputs.
 *
 * @param value - The value to convert (can be string, number, or undefined)
 * @returns The converted number, or null if the value cannot be parsed
 *
 * @example
 * ```typescript
 * stringToNumber('100') // Returns: 100
 * stringToNumber(200) // Returns: 200
 * stringToNumber('0') // Returns: 0
 * stringToNumber(0) // Returns: 0
 * stringToNumber('invalid') // Returns: null
 * stringToNumber(undefined) // Returns: null
 * stringToNumber('') // Returns: null
 * ```
 */
export const stringToNumber = (
    value: string | number | undefined
): number | null => {
    if (!value && value !== 0) return null
    const parsed = Number(value)
    return isNaN(parsed) ? null : parsed
}

/**
 * Escapes special regex characters in a string to make it safe for use in RegExp.
 *
 * This function escapes characters that have special meaning in regular expressions,
 * preventing regex syntax errors when the string is used in pattern matching.
 * This is particularly useful for search/highlight functionality where user input
 * may contain characters like parentheses, brackets, or other regex metacharacters.
 *
 * @param text - The string to escape
 * @returns The escaped string safe for use in RegExp
 *
 * @example
 * ```typescript
 * escapeRegexCharacters('hello (world)') // Returns: 'hello \\(world\\)'
 * escapeRegexCharacters('test [123]') // Returns: 'test \\[123\\]'
 * escapeRegexCharacters('$100.00') // Returns: '\\$100\\.00'
 * escapeRegexCharacters('normal text') // Returns: 'normal text'
 * ```
 */
export const escapeRegexCharacters = (text: string): string => {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Escapes the tilde character in search text for backend database queries.
 *
 * The tilde (~) is a regex operator in PostgreSQL and some other databases.
 * This function escapes it so that when users search for nodes containing the
 * literal tilde character, it's treated as text rather than a pattern matching operator.
 * The escaping is done internally - users still see ~ in the search box.
 *
 * @param text - The search text to escape
 * @returns The escaped string safe for database queries
 *
 * @example
 * ```typescript
 * escapeSearchText('test~value') // Returns: 'test\\~value'
 * escapeSearchText('normal text') // Returns: 'normal text'
 * escapeSearchText('~') // Returns: '\\~'
 * ```
 */
export const escapeSearchText = (text: string): string => {
    // Escape tilde (~) which is a regex operator in PostgreSQL and other databases
    // This allows users to search for nodes that contain the literal ~ character
    return text.replace(/~/g, '\\~')
}

